/* eslint-disable no-use-before-define */
/* eslint-disable react/prop-types */
/* eslint-disable no-shadow */
/* eslint-disable no-param-reassign */
import { useEffect, useRef } from 'react';
import {
  useLazyDataModel, params, number, string, id
} from '@thd-nucleus/data-sources';
import { getChannelID, getCustomerInfo, isAuthError, redirectToLogin } from '../utils/general';
import { useRequestModels } from '../models/user-request-models';
import { ERROR } from '../utils/verbiage';

interface IRequestProps {
  queryName: string;
  dataModelOverride?: object;
}

export const useGiftCardRequest = ({ queryName, dataModelOverride }: IRequestProps) => {
  const promiseResolver = useRef<((...args: unknown[]) => unknown) | null>(null);
  const promiseRejecter = useRef<((...args: unknown[]) => unknown) | null>(null);
  const { customerAccountId, userId, jwt } = getCustomerInfo();

  const { isMutation, requestContext, responseModel } = useRequestModels[queryName];

  const giftCardRequestContext = {
    channelId: number().isRequired(),
    userId: id().isRequired(),
    customerAccountId: id().isRequired(),
    hdInitiator: string().isRequired(),
    ...requestContext
  };

  const dataModel = isMutation
    ? {
      [queryName]: params(giftCardRequestContext).mutation().shape(responseModel)
    }
    : {
      [queryName]: params(giftCardRequestContext).shape(responseModel)
    };

  const opts = {
    dataModel: dataModelOverride || dataModel,
    context: {
      headers: {
        credentials: 'include',
        mode: 'cors',
        authorization: jwt
      }
    },
    ssr: false,
    fetchPolicy: 'no-cache'
  };

  const [getData, { loading, error, data }] = useLazyDataModel(queryName, opts);

  useEffect(() => {
    if (!loading && data) {
      if (!promiseResolver?.current) {
        throw Error('No promiseResolver');
      }
      if (data[queryName]) {
        promiseResolver.current(data[queryName]);
      }
    }

    if (!loading && error) {
      if (!promiseRejecter?.current) {
        throw Error('No promiseRejecter');
      }

      try {
        const gqlErrors = error.graphQLErrors?.[0];
        const errorExtensions = gqlErrors?.extensions;
        if (isAuthError(errorExtensions)) {
          setTimeout(() => redirectToLogin(), 100);
        }

        if (errorExtensions?.errors?.length || errorExtensions?.id) {
          const _error = errorExtensions.errors?.[0] || { errorMessage: ERROR.technicalError };
          promiseRejecter.current(_error);
        }
      } catch (err) {
        console.error(err);
      }
    }
  }, [queryName, promiseResolver, promiseRejecter, loading, data, error]);

  const makeRequest = (variables: object): Promise<T> => {
    return new Promise((resolve, reject) => {
      promiseResolver.current = resolve;
      promiseRejecter.current = reject;
      getData({
        variables: {
          channelId: getChannelID(),
          userId,
          customerAccountId,
          hdInitiator: 'CARDS_ACCOUNTS',
          ...variables
        }
      });
    });
  };

  return {
    makeRequest,
    loading,
    data,
    error
  };
};
